"use client";
/**
 * One edible thing as its own object, in whichever of the three compositions is
 * selected.
 *
 * A Record rather than a switch: adding a variant is one member and one entry,
 * and the compiler refuses a member without a component.
 *
 * Both selections come from the shared variant registry, so the tweak panel
 * drives every family through one provider. An explicit prop still wins,
 * because an embedder that pins a composition is not asking the panel's
 * opinion.
 */
import type { ComponentType, ReactElement } from "react";
import { useCresultcardVariant } from "@/components/ui/Cresultcard";
import {
  C_NUTRITION_CARD_VARIANTS,
  C_NUTRITION_CARD_VARIANT_GROUP,
  C_NUTRITION_SERVING_SCOPES,
  C_NUTRITION_SERVING_SCOPE_GROUP,
} from "./cnutritioncard.constants";
import { CnutritioncardLabelLine } from "./CnutritioncardLabelLine";
import { CnutritioncardMacroBoard } from "./CnutritioncardMacroBoard";
import { CnutritioncardPlateCard } from "./CnutritioncardPlateCard";
import {
  CnutritioncardVariant,
  type CnutritioncardProps,
  type CnutritioncardVariantProps,
} from "./cnutritioncard.types";

const VARIANT_COMPONENT: Record<
  CnutritioncardVariant,
  ComponentType<CnutritioncardVariantProps>
> = {
  [CnutritioncardVariant.LabelLine]: CnutritioncardLabelLine,
  [CnutritioncardVariant.PlateCard]: CnutritioncardPlateCard,
  [CnutritioncardVariant.MacroBoard]: CnutritioncardMacroBoard,
};

export function Cnutritioncard({
  data,
  variant,
  servingScope,
  openControl,
}: CnutritioncardProps): ReactElement {
  const { value: registryVariant } = useCresultcardVariant({
    group: C_NUTRITION_CARD_VARIANT_GROUP,
    options: C_NUTRITION_CARD_VARIANTS,
  });
  const { value: registryScope } = useCresultcardVariant({
    group: C_NUTRITION_SERVING_SCOPE_GROUP,
    options: C_NUTRITION_SERVING_SCOPES,
  });
  const VariantComponent = VARIANT_COMPONENT[variant ?? registryVariant];

  return (
    <VariantComponent
      data={data}
      openControl={openControl}
      servingScope={servingScope ?? registryScope}
    />
  );
}
